import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import api from '../../services/api';
import { Rack } from './merchandisingTypes';
import { readNokeError, RestockLine, variantLabel } from './restockPlan';

export type RackSourceLine = Pick<RestockLine, 'sku' | 'title' | 'variant_id' | 'color' | 'size' | 'qty' | 'source_tag'>;
const pieces = (lines: RackSourceLine[]) => lines.reduce((sum, line) => sum + line.qty, 0);
const knownStock = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

/** Sourcing preparation uses full plan gaps; executable replenishment remains capacity-limited. */
export function allocateRackOrders(racks: Rack[], orders: RestockLine[]): Record<string, RackSourceLine[]> {
  const remaining = orders.map((line) => ({ ...line }));
  const calculated = new Set<string>();
  return Object.fromEntries(racks.map((rack) => {
    const selected: RackSourceLine[] = [];
    rack.items.forEach((item) => item.variants.forEach((variant) => {
      const key = `${item.sku}:${variant.variant_id}`;
      if (calculated.has(key) || variant.can_order === false) return;
      const matching = remaining.filter((line) => line.sku === item.sku && Number(line.variant_id) === Number(variant.variant_id));
      const office = variant.office; const transit = variant.transit;
      if (variant.can_order === true && knownStock(office) && knownStock(transit) && knownStock(variant.on_hand) && knownStock(variant.target)) {
        const qty = Math.max(0, variant.target - variant.on_hand - transit - office);
        if (qty > 0) selected.push({ sku: item.sku, title: item.title, variant_id: variant.variant_id, color: variant.color, size: variant.size, qty, source_tag: matching[0]?.source_tag || null });
        matching.forEach((line) => { line.qty = 0; });
        calculated.add(key);
        return;
      }
      // Older payloads cannot establish a full purchase gap; retain only already supplied order quantities.
      let gap = Math.max(0, variant.needed);
      matching.forEach((line) => {
        const qty = Math.min(gap, line.qty);
        if (qty > 0) selected.push({ ...line, qty });
        line.qty -= qty; gap -= qty;
      });
    }));
    return [rack.id, selected];
  }));
}

export function safeSinsangUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
    if (url.hostname !== 'sinsangmarket.kr') return null;
    return url.href;
  } catch { return null; }
}

interface SourcingItem { sku: string; links: string[]; status: 'linked' | 'missing' }
const RackOrderLinks: React.FC<{ rack: Rack; lines: RackSourceLine[]; proposed: boolean }> = ({ rack, lines, proposed }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState<SourcingItem[]>([]);
  const [opened, setOpened] = useState<string[]>([]);
  const [blocked, setBlocked] = useState(false);
  const grouped = new Map<string, RackSourceLine[]>();
  lines.forEach((line) => grouped.set(line.sku, [...(grouped.get(line.sku) || []), line]));
  const groups = Array.from(grouped.entries()).map(([sku, rows]) => ({ sku, lines: rows }));
  const load = async () => {
    setOpen(true); setBusy(true); setError(''); setItems([]); setOpened([]); setBlocked(false);
    try {
      const { data } = await api.post('/api/noke/merchandising/sourcing-links', { racks: [{ id: rack.id, items: groups.map((group) => ({ sku: group.sku,
        variants: group.lines.map((line) => ({ color: line.color, size: line.size, qty: line.qty })),
      })) }] });
      if (data.allowed !== true) throw new Error('沒有查看連結的權限。');
      const row = data.racks.find((entry: { id: string }) => entry.id === rack.id);
      setItems((row?.items || []).filter((item: SourcingItem) => groups.some((group) => group.sku === item.sku))
        .map((item: SourcingItem) => ({ ...item, links: Array.from(new Set(item.links.map(safeSinsangUrl).filter((url): url is string => !!url))) })));
    } catch (err) { setError(readNokeError(err, '無法讀取連結。')); }
    finally { setBusy(false); }
  };
  const links = Array.from(new Set(items.map((item) => item.links[0]).filter(Boolean)));
  const remaining = links.filter((url) => !opened.includes(url));
  const openBatch = () => {
    const completed: string[] = [];
    setBlocked(false);
    for (const url of remaining.slice(0, 10)) {
      const tab = window.open('about:blank', '_blank');
      if (!tab) { setBlocked(true); break; }
      try { tab.opener = null; tab.location.replace(url); completed.push(url); }
      catch { tab.close(); setBlocked(true); break; }
    }
    setOpened((old) => [...old, ...completed]);
  };
  if (proposed) return <Typography variant="caption" color="text.secondary">採用這版後產生訂貨清單</Typography>;
  return <>
    <Button size="small" disabled={!lines.length} onClick={load} aria-label={`${rack.label} 連結 ${pieces(lines)} 件`}>連結 · {pieces(lines)} 件</Button>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{rack.label} · 待訂 {pieces(lines)} 件</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>只開啟供應商商品頁，不會下單或記錄已訂。未登錄的韓國訂單不在計算內，請先核對是否已訂。</Alert>
        <Typography variant="body2" sx={{ mb: 1 }}>依配置缺口列出待訂數量，已扣除門市、辦公室與在途庫存。空間不足仍可先查看連結，請在到貨前安排好陳列空間。</Typography>
        <Typography variant="body2" color="text.secondary">每款批次只開主要連結；備選連結請視需要另行開啟。</Typography>
        {busy && <Typography role="status">正在取得連結…</Typography>}
        {error && <Alert severity="error">{error}</Alert>}
        {groups.map((group) => <Box key={group.sku} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography fontWeight={600}>{group.sku} · {group.lines[0].title}</Typography>
          {group.lines.map((line, index) => <Typography key={index} variant="body2">{variantLabel(line.color, line.size)} × {line.qty} 件 · {line.source_tag || '貨源待確認'}</Typography>)}
          {!busy && items.find((item) => item.sku === group.sku)?.links.map((url, index) => <Link key={url} href={url} target="_blank" rel="noopener noreferrer" sx={{ mr: 2 }}>{index === 0 ? '主要連結' : `備選連結 ${index}`}</Link>)}
          {!busy && !error && !items.find((item) => item.sku === group.sku)?.links.length && <Typography variant="caption">尚無可用的已確認連結，請人工核對貨源。</Typography>}
        </Box>)}
        {blocked && <Alert severity="warning" sx={{ mt: 2 }}>瀏覽器阻擋了分頁。請允許此網站開啟彈出式視窗後重試，或逐一點上方商品連結；未開啟的連結仍保留。</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>關閉</Button>{!busy && !error && !links.length ? <Button disabled>尚無可用的 Sinsang 連結</Button> : <Button variant="contained" disabled={busy || !!error || !remaining.length} onClick={openBatch}>開啟下 {Math.min(10, remaining.length)} 個連結（{opened.length}／{links.length}）</Button>}</DialogActions>
    </Dialog>
  </>;
};

export default RackOrderLinks;
